import {
  createContext,
  FC,
  ReactElement,
  useContext,
  useMemo,
  useState,
} from 'react';
import { omitBy } from 'lodash-es';
import { Box } from '@chakra-ui/react';

import { useOpenClose, UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  DocumentFilter,
  Nullable,
  Option,
  OptionValue,
  Sorting,
  TenantCustomComponentContent,
} from '~/core/shared/types';
import { buildComponent } from '~/core/shared/utils/general';
import {
  TenantDocumentListView,
  TenantDocumentListViewFilter,
  TenantDocumentObjectDefinition,
  TenantDocumentRouteProps,
} from '../../config';
import { DefinitionContext } from '../../contexts';
import { useMiddleOfficeConfig } from '../../contexts/middleOfficeConfig';
import { useFiltersQuery } from '../../hooks/useFiltersQuery';
import { ApplicationsFiltersDrawer } from '../ApplicationsFiltersDrawer.component';
import { SaveFilterModal } from '../filters/SaveFilterModal.component';
import { Layout } from '../Layout.component';
import { DocumentsTable } from '../table/DocumentsTable.component';
import { ApplicationsSubheader } from './subheaders/Applications.component';

type DocumentListContextType = {
  chosenFilter: Nullable<DocumentFilter>;
  filters: Record<string, string | string[] | undefined>;
  setFilter: (key: string, value: string) => void;
  setFilters: (filters: DocumentListContextType['filters']) => void;
  filtersDrawerApi: UseOpenCloseReturn;
  saveFilterModalApi: UseOpenCloseReturn;
  sorting: Sorting;
  setChosenFilter: (filter: Nullable<DocumentFilter>) => void;
  setSorting: (sorting: Sorting) => void;
  savedFilters: DocumentFilter[] | undefined;
  setStateFilters: (filters: Record<string, string>) => void;
  selectedOptions: Record<string, Option<OptionValue>[]>;
  setSelectedOptions: (key: string, options: Option<OptionValue>[]) => void;
};

export const DocumentListContext = createContext<DocumentListContextType>(
  {} as DocumentListContextType,
);

type TenantDocumentListViewContext = {
  config: TenantDocumentListView<TenantDocumentObjectDefinition>;
};

export const DocumentListViewContext =
  createContext<TenantDocumentListViewContext>(
    {} as TenantDocumentListViewContext,
  );

const buildFilterComponent = (
  filter: TenantDocumentListViewFilter,
  props?: any,
): ReactElement | undefined => {
  if (filter.type === 'builder') {
    // @TODO
  } else if (filter.type === 'custom') {
    return buildComponent(filter as TenantCustomComponentContent, props);
  }
  return undefined;
};

const defaultSorting: Sorting = {
  column: 'updatedAt',
  direction: 'desc',
};

export const DocumentsList: FC<TenantDocumentRouteProps> = (props) => {
  const { tenantNavigationItem } = props;
  const middleOfficeConfig = useMiddleOfficeConfig();
  const viewConfig = middleOfficeConfig.getViewConfig(
    props,
  ) as TenantDocumentListView<TenantDocumentObjectDefinition>;
  const definition =
    viewConfig.documentRouteProps?.tenantDocumentDefinition ||
    'unknownDefinition';

  const [selectedOptions, setSelectedOptionsState] = useState<
    Record<string, Option<OptionValue>[]>
  >({});
  const [filters, setStateFilters] = useState<
    Record<string, string | string[]>
  >({});
  const [sorting, setSorting] = useState<Sorting>(defaultSorting);
  const [chosenFilter, setChosenFilter] =
    useState<Nullable<DocumentFilter>>(null);
  const saveFilterModalApi = useOpenClose();
  const filtersDrawerApi = useOpenClose();

  const setSelectedOptions = (key: string, options: Option<OptionValue>[]) => {
    setSelectedOptionsState((prev) => ({
      ...prev,
      [key]: options,
    }));
  };

  const setFilters = (
    updatedFilters: Record<string, string | string[]>,
  ): void => {
    setStateFilters({
      ...filters,
      ...omitBy(updatedFilters, (value, key) => value === undefined),
    });
  };

  const setFilter = (key: string, value: string): void => {
    setStateFilters({
      ...filters,
      ...{ [key]: value },
    });
  };

  const filtersDrawer: ReactElement | undefined = useMemo(() => {
    if (viewConfig.filter) {
      const filterComponent = buildFilterComponent(viewConfig.filter);

      if (filterComponent) {
        return <ApplicationsFiltersDrawer contentComponent={filterComponent} />;
      }
    }
    return undefined;
  }, [viewConfig.filter]);

  const { filters: savedFilters } = useFiltersQuery(definition);

  const definitionContextValue = useMemo(
    () => ({
      definition:
        viewConfig.documentRouteProps?.tenantDocumentDefinition ||
        'definitionNotSet',
    }),
    [viewConfig.documentRouteProps?.tenantDocumentDefinition],
  );

  const documentListViewContextValue = useMemo(
    () => ({
      config: viewConfig,
    }),
    [viewConfig],
  );

  const documentListContextValue = useMemo(
    () => ({
      chosenFilter,
      filters,
      savedFilters,
      selectedOptions,
      setChosenFilter,
      setFilter,
      setFilters,
      setSorting,
      setStateFilters,
      setSelectedOptions,
      sorting,
      filtersDrawerApi,
      saveFilterModalApi,
    }),
    [
      chosenFilter,
      filters,
      savedFilters,
      selectedOptions,
      setChosenFilter,
      setFilter,
      setFilters,
      setSorting,
      setStateFilters,
      setSelectedOptions,
      sorting,
      filtersDrawerApi,
      saveFilterModalApi,
    ],
  );

  return (
    <DefinitionContext.Provider value={definitionContextValue}>
      <DocumentListViewContext.Provider value={documentListViewContextValue}>
        <DocumentListContext.Provider value={documentListContextValue}>
          <Layout subheader={<ApplicationsSubheader />}>
            <SaveFilterModal />

            <Box p={12}>
              <DocumentsTable
                columns={viewConfig.columns}
                defaultSorting={defaultSorting}
                rowPath={viewConfig.rowPath}
              />

              {filtersDrawer}
            </Box>
          </Layout>
        </DocumentListContext.Provider>
      </DocumentListViewContext.Provider>
    </DefinitionContext.Provider>
  );
};
